import { Hash, Search, X } from 'lucide-react';
import { ANY, parseFleetSearch } from '../../../core/fleet-filters';
import type { VehicleFilters } from '../../../core/fleet-filters';
import {
  VEHICLE_CATEGORIES,
  VEHICLE_CATEGORY_LABELS,
  VEHICLE_TYPES,
  VEHICLE_TYPE_LABELS,
} from '../../../types/fleet';

interface VehicleFilterBarProps {
  filters: VehicleFilters;
  onChange: (next: VehicleFilters) => void;
  /** How many vehicles the current filters keep, for the live hint line. */
  resultCount: number;
}

const selectClass =
  'h-10 rounded-xl border border-line bg-panel px-3 text-sm text-ink/80 focus:outline-none focus:border-brand-500';

/**
 * Search + filters for the vehicle roster. A query that resolves to a
 * three-digit internal number is called out, because that is the lookup the
 * dispatcher runs all day and the grid highlights its hit.
 */
export function VehicleFilterBar({ filters, onChange, resultCount }: VehicleFilterBarProps) {
  const query = parseFleetSearch(filters.text);
  const set = <K extends keyof VehicleFilters>(key: K, value: VehicleFilters[K]) =>
    onChange({ ...filters, [key]: value });

  return (
    <div className="bg-panel border border-line rounded-2xl p-3 sm:p-4 space-y-3">
      <div className="relative">
        <Search
          size={18}
          className="absolute top-1/2 -translate-y-1/2 right-3 text-ink/40"
          aria-hidden
        />
        <input
          type="search"
          value={filters.text}
          onChange={(event) => set('text', event.target.value)}
          placeholder="חיפוש לפי מספר פנימי, מספר צ מלא או שם רכב"
          aria-label="חיפוש רכב"
          className="w-full h-11 rounded-xl border border-line bg-canvas pr-10 pl-10 text-sm focus:outline-none focus:border-brand-500 focus:bg-panel"
        />
        {filters.text ? (
          <button
            type="button"
            onClick={() => set('text', '')}
            title="ניקוי חיפוש"
            className="absolute top-1/2 -translate-y-1/2 left-2 w-7 h-7 rounded-lg flex items-center justify-center text-ink/40 hover:text-ink"
          >
            <X size={16} aria-hidden />
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          value={filters.category}
          onChange={(event) => set('category', event.target.value as VehicleFilters['category'])}
          aria-label="סינון לפי קטגוריה"
          className={selectClass}
        >
          <option value={ANY}>כל הקטגוריות</option>
          {VEHICLE_CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {VEHICLE_CATEGORY_LABELS[category]}
            </option>
          ))}
        </select>

        <select
          value={filters.vehicleType}
          onChange={(event) =>
            set('vehicleType', event.target.value as VehicleFilters['vehicleType'])
          }
          aria-label="סינון לפי סוג רכב"
          className={selectClass}
        >
          <option value={ANY}>כל סוגי הרכב</option>
          {VEHICLE_TYPES.map((type) => (
            <option key={type} value={type}>
              {VEHICLE_TYPE_LABELS[type]}
            </option>
          ))}
        </select>
      </div>

      <p className="text-xs text-ink/50 flex items-center gap-1.5">
        {query.militaryId ? (
          <>
            <Hash size={13} className="text-brand-500" aria-hidden />
            <span>
              חיפוש מספר פנימי{' '}
              <span className="font-bold text-brand-700" data-dir="ltr">
                {query.militaryId}
              </span>
            </span>
            <span aria-hidden>·</span>
          </>
        ) : null}
        <span>{resultCount} רכבים מוצגים</span>
      </p>
    </div>
  );
}
